import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Video, RotateCcw, Check, Mic, MicOff, 
  Camera, CameraOff, AlertCircle, Loader2, ArrowRight,
  Building2, User, Mail, Play, Clock, Key, Users, Download,
  Phone, Globe, Briefcase, FileText, Shield, CheckCircle2, Home
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Booth Mode Steps
const STEPS = {
  WELCOME: 'welcome',
  CODE_ENTRY: 'code_entry',
  FORM: 'form',
  DECLARATION: 'declaration',  // New step for legal declaration
  PROMPT: 'prompt',
  COUNTDOWN: 'countdown',
  RECORDING: 'recording',
  PREVIEW: 'preview',
  SUBMITTING: 'submitting',
  THANKYOU: 'thankyou'
};

// Video purpose options
const VIDEO_PURPOSES = [
  { value: 'company_intro', label: 'Company Introduction' },
  { value: 'product_demo', label: 'Product/Service Demo' },
  { value: 'testimonial', label: 'Customer Testimonial' },
  { value: 'event_promotion', label: 'Event Promotion' },
  { value: 'thought_leadership', label: 'Thought Leadership' },
  { value: 'other', label: 'Other' }
];

// Recording prompts that rotate
const RECORDING_PROMPTS = [
  "Tell us about your company in 45 seconds",
  "What brings you to this trade show?",
  "What's your company's biggest achievement this year?",
  "Describe your product or service in 45 seconds"
];

export default function BoothMode() {
  // Current step
  const [step, setStep] = useState(STEPS.WELCOME);
  
  // Access code state
  const [accessCode, setAccessCode] = useState('');
  const [eventInfo, setEventInfo] = useState(null);
  const [codeError, setCodeError] = useState('');
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  
  // Form data - Enhanced with more user information
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    mobile: '',
    designation: '',
    purpose: '',
    website: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Declaration acceptance
  const [declarationAccepted, setDeclarationAccepted] = useState({
    authority: false,
    accuracy: false,
    prohibited: false,
    rights: false,
    indemnity: false,
    data_processing: false
  });
  const [declarationAlreadyAccepted, setDeclarationAlreadyAccepted] = useState(false);
  const [checkingDeclaration, setCheckingDeclaration] = useState(false);
  
  // Recording prompt
  const [currentPrompt, setCurrentPrompt] = useState(RECORDING_PROMPTS[0]);
  
  // Countdown
  const [countdown, setCountdown] = useState(3);
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const MAX_DURATION = 45; // 45 seconds max (final video ~60 sec with branding)
  const MIN_BEFORE_STOP = 30; // Stop button appears after 30 seconds
  const WARN_AT = 40; // Warning appears at 40 seconds
  
  // Media states
  const [stream, setStream] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [error, setError] = useState(null);
  
  // Branding status states
  const [submissionId, setSubmissionId] = useState(null);
  const [brandingStatus, setBrandingStatus] = useState(null);
  const [brandedVideoUrl, setBrandedVideoUrl] = useState(null);
  
  // Refs
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const brandingPollRef = useRef(null);

  // Initialize camera
  const initCamera = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setHasPermission(false);
        setError('Your browser does not support video recording.');
        return;
      }

      // First, enumerate devices to check if camera exists
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      if (videoDevices.length === 0) {
        setHasPermission(false);
        setError('No camera found. Please connect a camera and try again.');
        return;
      }

      let mediaStream;
      
      // Try multiple constraint configurations
      const constraintOptions = [
        // Option 1: Basic with user-facing camera
        { video: { facingMode: 'user' }, audio: true },
        // Option 2: Just video and audio, no constraints
        { video: true, audio: true },
        // Option 3: Specific device (first available)
        { video: { deviceId: videoDevices[0].deviceId }, audio: true },
        // Option 4: Video only (in case audio is the problem)
        { video: true, audio: false },
      ];

      for (const constraints of constraintOptions) {
        try {
          console.log('Trying camera constraints:', constraints);
          mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
          console.log('Camera access successful with:', constraints);
          break; // Success! Exit loop
        } catch (err) {
          console.log('Failed with constraints:', constraints, err.name);
          continue; // Try next option
        }
      }

      if (!mediaStream) {
        throw new Error('Could not access camera with any settings');
      }
      
      setStream(mediaStream);
      setHasPermission(true);
      setError(null);
      // Note: srcObject is set via useEffect when stream and videoRef are both available
    } catch (err) {
      console.error('Camera access error:', err);
      setHasPermission(false);
      
      const errorMessage = err.message || '';
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera access blocked. Click the lock icon in your browser\'s address bar, set Camera to "Allow", then click Retry below.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found. Please connect a camera and try again.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError' || errorMessage.includes('timeout') || errorMessage.includes('Could not start')) {
        setError('Camera is busy or unavailable. Please close other apps using the camera (Zoom, Teams, etc.) and click Retry.');
      } else if (err.name === 'OverconstrainedError') {
        setError('Camera settings not supported. Please try a different device.');
      } else if (err.name === 'AbortError' || errorMessage.includes('aborted')) {
        setError('Camera request was interrupted. Please click Retry to try again.');
      } else {
        setError(`Camera error: ${errorMessage || 'Unknown error'}. Please close other camera apps and try again.`);
      }
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [stream]);

  // Attach stream to video element when both are available
  useEffect(() => {
    if (stream && videoRef.current && (step === STEPS.PROMPT || step === STEPS.RECORDING)) {
      videoRef.current.srcObject = stream;
      // Try to play the video (handles autoplay restrictions on mobile)
      videoRef.current.play().catch(err => {
        console.log('Video autoplay prevented:', err);
        // This is usually fine - user interaction will start it
      });
    }
  }, [stream, step]);

  // Start countdown then recording
  const startCountdown = () => {
    setStep(STEPS.COUNTDOWN);
    setCountdown(3);
    
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start recording
  const startRecording = () => {
    if (!stream) return;
    
    chunksRef.current = [];
    setDuration(0);
    setStep(STEPS.RECORDING);
    
    // Determine best supported mimeType for cross-browser compatibility
    let mimeType = 'video/webm;codecs=vp9,opus';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm;codecs=vp8,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/mp4'; // Safari/iOS fallback
        }
      }
    }
    
    const mediaRecorder = new MediaRecorder(stream, { mimeType });
    
    // Store reference to this specific recorder for ownership checks
    const thisRecorder = mediaRecorder;
    
    mediaRecorder.ondataavailable = (e) => {
      // Only process data if this is still the active recorder
      if (mediaRecorderRef.current !== thisRecorder) {
        console.log('Ignoring stale ondataavailable from orphan recorder');
        return;
      }
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
        console.log('Chunk received, size:', e.data.size, 'total chunks:', chunksRef.current.length);
      }
    };
    
    mediaRecorder.onstop = () => {
      // Only transition to preview if this is still the active recorder
      if (mediaRecorderRef.current !== thisRecorder) {
        console.log('Ignoring stale onstop from orphan recorder');
        return;
      }
      console.log('MediaRecorder stopped, total chunks:', chunksRef.current.length);
      const blob = new Blob(chunksRef.current, { type: mimeType });
      console.log('Blob created, size:', blob.size);
      setRecordedBlob(blob);
      // Revoke old URL if exists to free memory
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
      setRecordedUrl(URL.createObjectURL(blob));
      // Only navigate to preview if we're currently in RECORDING state
      // This prevents stale handlers from overriding other navigation
      setStep(prev => prev === STEPS.RECORDING ? STEPS.PREVIEW : prev);
    };
    
    mediaRecorderRef.current = mediaRecorder;
    // Start recording - use smaller timeslice for more frequent data collection
    mediaRecorder.start(500); // Collect data every 500ms for better reliability
    setIsRecording(true);
    
    // Timer with direct MediaRecorder access to avoid stale closure issues
    timerRef.current = setInterval(() => {
      setDuration(prev => {
        const newDuration = prev + 1;
        if (newDuration >= MAX_DURATION) {
          // Stop directly here to avoid closure issues
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            // Request any remaining data before stopping
            mediaRecorderRef.current.requestData();
            mediaRecorderRef.current.stop();
            setIsRecording(false);
          }
          return MAX_DURATION;
        }
        return newDuration;
      });
    }, 1000);
  };

  // Stop recording - use MediaRecorder state instead of React state to avoid closure issues
  const stopRecording = useCallback(() => {
    // Clear timer first to prevent multiple calls
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Check MediaRecorder state directly (not React state which can be stale in closures)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      // Request any remaining data before stopping
      mediaRecorderRef.current.requestData();
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  // Retake recording - reset recording state but keep form data and camera
  const handleRetake = () => {
    // Set ref to null FIRST to invalidate any stale onstop/ondataavailable handlers
    mediaRecorderRef.current = null;
    
    // Clear recorded video
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl); // Free memory
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setDuration(0);
    setIsRecording(false);
    setError(null);
    
    // Clear recording refs
    chunksRef.current = [];
    
    // Go back to prompt screen (camera should still be available)
    setStep(STEPS.PROMPT);
  };

  // Toggle mute
  const toggleMute = () => {
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  // Toggle camera
  const toggleCamera = () => {
    if (stream) {
      stream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsCameraOff(!isCameraOff);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Validate form - Enhanced validation
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email format';
    else if (/gmail\.com|yahoo\.com|hotmail\.com|outlook\.com/i.test(formData.email)) {
      errors.email = 'Please use your official work email';
    }
    if (!formData.company.trim()) errors.company = 'Company is required';
    if (!formData.mobile.trim()) errors.mobile = 'Mobile number is required';
    else if (!/^[\d\s\-\+\(\)]{10,15}$/.test(formData.mobile.replace(/\s/g, ''))) {
      errors.mobile = 'Invalid mobile number';
    }
    if (!formData.designation.trim()) errors.designation = 'Designation is required';
    if (!formData.purpose) errors.purpose = 'Please select the purpose';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Validate declaration - all checkboxes must be accepted
  const validateDeclaration = () => {
    return Object.values(declarationAccepted).every(v => v === true);
  };

  // Validate access code
  const validateAccessCode = async () => {
    if (!accessCode.trim()) {
      setCodeError('Please enter an access code');
      return;
    }
    
    setIsValidatingCode(true);
    setCodeError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/booth/validate-code`, {
        access_code: accessCode.toUpperCase()
      });
      
      setEventInfo(response.data);
      
      if (!response.data.can_record) {
        setCodeError('Daily recording limit reached for this event');
        return;
      }
      
      setStep(STEPS.FORM);
    } catch (err) {
      setCodeError(err.response?.data?.detail || 'Invalid access code');
    } finally {
      setIsValidatingCode(false);
    }
  };

  // Handle form submit - check declaration status and go to appropriate step
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    // Check if user already accepted declaration (logged in users)
    setCheckingDeclaration(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/video-declaration-status`, { withCredentials: true });
      if (response.data.accepted) {
        // Declaration already accepted - skip to prompt
        setDeclarationAlreadyAccepted(true);
        const randomPrompt = RECORDING_PROMPTS[Math.floor(Math.random() * RECORDING_PROMPTS.length)];
        setCurrentPrompt(randomPrompt);
        setStep(STEPS.PROMPT);
        setTimeout(() => {
          initCamera();
        }, 100);
      } else {
        // Need to show declaration
        setStep(STEPS.DECLARATION);
      }
    } catch (err) {
      // If not logged in or error, show declaration anyway
      setStep(STEPS.DECLARATION);
    } finally {
      setCheckingDeclaration(false);
    }
  };
  
  // Handle declaration accept - save to backend and proceed to recording
  const handleDeclarationAccept = async () => {
    if (!validateDeclaration()) return;
    
    // Save declaration to user account if logged in
    try {
      await axios.post(`${API_URL}/api/auth/accept-video-declaration`, {}, { withCredentials: true });
    } catch (err) {
      // Continue even if save fails (guest users)
      console.log('Declaration save skipped (guest user or error)');
    }
    
    // Pick a random prompt and proceed
    const randomPrompt = RECORDING_PROMPTS[Math.floor(Math.random() * RECORDING_PROMPTS.length)];
    setCurrentPrompt(randomPrompt);
    setStep(STEPS.PROMPT);
    setTimeout(() => {
      initCamera();
    }, 100);
  };

  // Submit video
  const handleSubmit = async () => {
    if (!recordedBlob) return;
    
    setStep(STEPS.SUBMITTING);
    setBrandingStatus('pending');
    setBrandedVideoUrl(null);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('video', recordedBlob, 'booth-recording.webm');
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('mobile', formData.mobile);
      formDataToSend.append('designation', formData.designation);
      formDataToSend.append('purpose', formData.purpose);
      formDataToSend.append('website', formData.website || '');
      formDataToSend.append('prompt', currentPrompt);
      formDataToSend.append('duration', duration);
      formDataToSend.append('declaration_accepted', 'true');
      axios.post(`${API_URL}/api/consents`, { kind: 'booth_data_processing', details: { name: formData.name, email: formData.email, company: formData.company } }, { withCredentials: true }).catch(() => {});
      if (accessCode) {
        formDataToSend.append('access_code', accessCode);
      }
      
      const response = await axios.post(`${API_URL}/api/booth/submit`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Store submission ID for branding status polling
      const newSubmissionId = response.data.submission_id;
      setSubmissionId(newSubmissionId);
      
      // Start polling for branding status
      if (newSubmissionId) {
        startBrandingPoll(newSubmissionId);
      }
      
      // Refresh event info to update remaining count
      if (accessCode && eventInfo) {
        try {
          const refreshResponse = await axios.post(`${API_URL}/api/booth/validate-code`, {
            access_code: accessCode
          });
          setEventInfo(refreshResponse.data);
        } catch (e) {
          // Ignore refresh errors
        }
      }
      
      setStep(STEPS.THANKYOU);
    } catch (err) {
      console.error('Submit error:', err);
      if (err.response?.status === 429) {
        setError('Daily recording limit reached for this event.');
      } else {
        setError('Failed to submit. Please try again.');
      }
      setStep(STEPS.PREVIEW);
    }
  };
  
  // Poll for branding status
  const startBrandingPoll = (sid) => {
    // Clear any existing poll
    if (brandingPollRef.current) {
      clearInterval(brandingPollRef.current);
    }
    
    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/booth/submission/${sid}/status`);
        const status = response.data.branding_status;
        setBrandingStatus(status);
        
        if (response.data.branded_video_url) {
          setBrandedVideoUrl(response.data.branded_video_url);
        }
        
        // Stop polling when complete, failed, or skipped
        if (['complete', 'failed', 'error', 'skipped'].includes(status)) {
          if (brandingPollRef.current) {
            clearInterval(brandingPollRef.current);
            brandingPollRef.current = null;
          }
        }
      } catch (e) {
        console.error('Branding poll error:', e);
      }
    };
    
    // Poll immediately, then every 3 seconds
    pollStatus();
    brandingPollRef.current = setInterval(pollStatus, 3000);
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (brandingPollRef.current) {
        clearInterval(brandingPollRef.current);
      }
    };
  }, []);

  // Record another - full state reset
  const handleRecordAnother = () => {
    // CRITICAL: Set ref to null FIRST to invalidate any stale onstop/ondataavailable handlers
    // This must happen BEFORE stopping tracks, as stopping tracks triggers final events
    const oldRecorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null; // Stale handlers will now bail due to ownership check
    
    // Clear any running timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    
    // Stop branding poll
    if (brandingPollRef.current) {
      clearInterval(brandingPollRef.current);
      brandingPollRef.current = null;
    }
    
    // Stop the old recorder if it was still recording
    if (oldRecorder && oldRecorder.state === 'recording') {
      oldRecorder.stop(); // This will trigger onstop, but it will bail due to ref being null
    }
    
    // Stop and release camera stream (this may also trigger stale events, but they'll bail)
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    // Reset form data
    setFormData({ name: '', email: '', company: '' });
    setFormErrors({});
    
    // Reset recording state
    setRecordedBlob(null);
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl); // Free memory
    }
    setRecordedUrl(null);
    setDuration(0);
    setIsRecording(false);
    
    // Reset branding state
    setSubmissionId(null);
    setBrandingStatus(null);
    setBrandedVideoUrl(null);
    
    // Reset camera state
    setHasPermission(null);
    setError(null);
    
    // Clear chunks ref
    chunksRef.current = [];
    
    // Keep access code and event info if using event mode
    // Go back to welcome screen (for new person) or form (if event mode)
    if (accessCode && eventInfo) {
      // Event mode: go to form for next person but keep event info
      setStep(STEPS.FORM);
    } else {
      setStep(STEPS.WELCOME);
    }
  };

  // Record another video (same person, keep details) - for event mode
  const handleRecordAnotherSamePerson = () => {
    // CRITICAL: Set ref to null FIRST
    const oldRecorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    
    // Clear timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    
    // Stop the old recorder if still recording
    if (oldRecorder && oldRecorder.state === 'recording') {
      oldRecorder.stop();
    }
    
    // Reset recording state but KEEP form data and camera
    setRecordedBlob(null);
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedUrl(null);
    setDuration(0);
    setIsRecording(false);
    setError(null);
    chunksRef.current = [];
    
    // Go back to prompt screen (camera should still be available)
    setStep(STEPS.PROMPT);
  };

  // ============== RENDER STEPS ==============

  // Welcome Screen
  if (step === STEPS.WELCOME) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-6" data-testid="booth-welcome">
        <div className="max-w-2xl w-full text-center">
          {/* Logo */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-black tracking-tight">
              <span className="text-white">BRAND</span>
              <span className="text-[#C41E3A]">STAGE</span>
            </h1>
            <p className="text-zinc-500 mt-2">Quick Bite Recording Booth</p>
          </div>
          
          {/* Hero */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 md:p-12 mb-8">
            <div className="w-24 h-24 bg-[#C41E3A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Video className="w-12 h-12 text-[#C41E3A]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Share Your Story</h2>
            <p className="text-zinc-400 text-lg mb-8 max-w-md mx-auto">
              Record a short video about your company and we will send it to you!
            </p>
            
            {/* Two options: Event Code or Record Free */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setStep(STEPS.CODE_ENTRY)}
                className="bg-[#C41E3A] hover:bg-[#A01830] text-white text-lg px-8 py-4 rounded-full font-bold transition-all transform hover:scale-105 inline-flex items-center justify-center gap-2"
                data-testid="booth-event-btn"
              >
                <Key className="w-5 h-5" />
                Enter Event Code
              </button>
              <button
                onClick={() => setStep(STEPS.FORM)}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-lg px-8 py-4 rounded-full font-bold transition-all inline-flex items-center justify-center gap-2"
                data-testid="booth-start-btn"
              >
                Record Free
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <p className="text-zinc-600 text-sm">
            Powered by BrandStage Media
          </p>
        </div>
      </div>
    );
  }

  // Code Entry Screen
  if (step === STEPS.CODE_ENTRY) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-6" data-testid="booth-code-entry">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#C41E3A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-[#C41E3A]" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Enter Event Code</h1>
            <p className="text-zinc-400">Enter the access code provided by your event organizer</p>
          </div>
          
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
            <div className="mb-6">
              <input
                type="text"
                value={accessCode}
                onChange={(e) => {
                  setAccessCode(e.target.value.toUpperCase());
                  setCodeError('');
                }}
                className={`w-full bg-zinc-800 border ${codeError ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-4 py-4 text-white text-center text-2xl font-mono tracking-widest placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A] uppercase`}
                placeholder="ENTER CODE"
                maxLength={8}
                data-testid="booth-code-input"
              />
              {codeError && <p className="text-red-500 text-sm mt-2 text-center">{codeError}</p>}
            </div>
            
            <button
              onClick={validateAccessCode}
              disabled={isValidatingCode}
              className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
              data-testid="booth-validate-code-btn"
            >
              {isValidatingCode ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Continue
                </>
              )}
            </button>
          </div>
          
          <button
            onClick={() => {
              setAccessCode('');
              setCodeError('');
              setStep(STEPS.WELCOME);
            }}
            className="w-full text-zinc-500 hover:text-zinc-300 mt-4 py-2 transition-colors"
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // Form Screen - Enhanced with more fields
  if (step === STEPS.FORM) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-6 overflow-y-auto" data-testid="booth-form">
        <div className="max-w-lg w-full my-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold mb-2">Your Information</h1>
            <p className="text-zinc-400">Please provide your details for verification</p>
            
            {/* Show event info if using access code */}
            {eventInfo && (
              <div className="mt-4 bg-[#C41E3A]/10 border border-[#C41E3A]/30 rounded-lg p-3">
                <p className="text-[#C41E3A] font-semibold">{eventInfo.event_name}</p>
                <p className="text-zinc-400 text-sm">
                  <Users className="w-4 h-4 inline mr-1" />
                  {eventInfo.recordings_remaining} of {eventInfo.daily_limit} recordings remaining today
                </p>
              </div>
            )}
          </div>
          
          <form onSubmit={handleFormSubmit} className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                  }}
                  className={`w-full bg-zinc-800 border ${formErrors.name ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]`}
                  placeholder="John Smith"
                  data-testid="booth-name-input"
                />
                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
              </div>
              
              {/* Designation */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  Designation *
                </label>
                <input
                  type="text"
                  value={formData.designation}
                  onChange={(e) => {
                    setFormData({ ...formData, designation: e.target.value });
                    if (formErrors.designation) setFormErrors({ ...formErrors, designation: '' });
                  }}
                  className={`w-full bg-zinc-800 border ${formErrors.designation ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]`}
                  placeholder="CEO / Marketing Head"
                  data-testid="booth-designation-input"
                />
                {formErrors.designation && <p className="text-red-500 text-xs mt-1">{formErrors.designation}</p>}
              </div>
              
              {/* Company */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => {
                    setFormData({ ...formData, company: e.target.value });
                    if (formErrors.company) setFormErrors({ ...formErrors, company: '' });
                  }}
                  className={`w-full bg-zinc-800 border ${formErrors.company ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]`}
                  placeholder="Acme Inc."
                  data-testid="booth-company-input"
                />
                {formErrors.company && <p className="text-red-500 text-xs mt-1">{formErrors.company}</p>}
              </div>
              
              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Company Website
                </label>
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
                  placeholder="www.company.com"
                  data-testid="booth-website-input"
                />
              </div>
              
              {/* Official Email */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Official Work Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (formErrors.email) setFormErrors({ ...formErrors, email: '' });
                  }}
                  className={`w-full bg-zinc-800 border ${formErrors.email ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]`}
                  placeholder="john@company.com"
                  data-testid="booth-email-input"
                />
                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
              </div>
              
              {/* Mobile */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => {
                    setFormData({ ...formData, mobile: e.target.value });
                    if (formErrors.mobile) setFormErrors({ ...formErrors, mobile: '' });
                  }}
                  className={`w-full bg-zinc-800 border ${formErrors.mobile ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]`}
                  placeholder="+91 98765 43210"
                  data-testid="booth-mobile-input"
                />
                {formErrors.mobile && <p className="text-red-500 text-xs mt-1">{formErrors.mobile}</p>}
              </div>
              
              {/* Purpose - Full width */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Purpose of Video *
                </label>
                <select
                  value={formData.purpose}
                  onChange={(e) => {
                    setFormData({ ...formData, purpose: e.target.value });
                    if (formErrors.purpose) setFormErrors({ ...formErrors, purpose: '' });
                  }}
                  className={`w-full bg-zinc-800 border ${formErrors.purpose ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#C41E3A]`}
                  data-testid="booth-purpose-select"
                >
                  <option value="">Select purpose...</option>
                  {VIDEO_PURPOSES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                {formErrors.purpose && <p className="text-red-500 text-xs mt-1">{formErrors.purpose}</p>}
              </div>
            </div>
            
            <p className="text-zinc-500 text-xs mt-4">* Required fields. Your information is used for verification and will not be shared.</p>
            
            <button
              type="submit"
              disabled={checkingDeclaration}
              className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-3 rounded-lg font-bold mt-6 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="booth-continue-btn"
            >
              {checkingDeclaration ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
          
          <button
            onClick={() => setStep(accessCode ? STEPS.CODE_ENTRY : STEPS.WELCOME)}
            className="w-full text-zinc-500 hover:text-zinc-300 mt-4 py-2 transition-colors"
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // Declaration Screen - Legal agreement before recording
  if (step === STEPS.DECLARATION) {
    const allAccepted = validateDeclaration();
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-6 overflow-y-auto" data-testid="booth-declaration">
        <div className="max-w-2xl w-full my-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#C41E3A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#C41E3A]" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Participant Declaration</h1>
            <p className="text-zinc-400">Please read and accept the following terms before recording</p>
          </div>
          
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
            {/* Declaration intro */}
            <div className="bg-zinc-800/50 rounded-lg p-4 mb-6 text-sm text-zinc-300">
              <p className="mb-2">By proceeding with your recording, you acknowledge and agree to the following:</p>
            </div>
            
            {/* Checkboxes */}
            <div className="space-y-4">
              {/* Authority */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={declarationAccepted.authority}
                    onChange={(e) => setDeclarationAccepted({...declarationAccepted, authority: e.target.checked})}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${declarationAccepted.authority ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'}`}>
                    {declarationAccepted.authority && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span className="text-sm text-zinc-300">
                  <strong className="text-white">Authority to Represent:</strong> I am authorized to speak on behalf of {formData.company || 'my company'} and make statements in this recording.
                </span>
              </label>
              
              {/* Accuracy */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={declarationAccepted.accuracy}
                    onChange={(e) => setDeclarationAccepted({...declarationAccepted, accuracy: e.target.checked})}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${declarationAccepted.accuracy ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'}`}>
                    {declarationAccepted.accuracy && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span className="text-sm text-zinc-300">
                  <strong className="text-white">Content Accuracy:</strong> All statements, claims, and information I provide are accurate to the best of my knowledge. I take full responsibility for the content of my recording.
                </span>
              </label>
              
              {/* Prohibited Content */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={declarationAccepted.prohibited}
                    onChange={(e) => setDeclarationAccepted({...declarationAccepted, prohibited: e.target.checked})}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${declarationAccepted.prohibited ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'}`}>
                    {declarationAccepted.prohibited && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span className="text-sm text-zinc-300">
                  <strong className="text-white">No Prohibited Content:</strong> My recording will not contain illegal, defamatory, misleading, fraudulent, discriminatory, obscene, or politically inflammatory content.
                </span>
              </label>
              
              {/* Rights & License */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={declarationAccepted.rights}
                    onChange={(e) => setDeclarationAccepted({...declarationAccepted, rights: e.target.checked})}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${declarationAccepted.rights ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'}`}>
                    {declarationAccepted.rights && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span className="text-sm text-zinc-300">
                  <strong className="text-white">Rights & Editorial Control:</strong> I grant BrandStage the right to edit, brand, and publish my recording. BrandStage may review, reject, or remove content that violates these terms.
                </span>
              </label>
              
              {/* Indemnity */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={declarationAccepted.indemnity}
                    onChange={(e) => setDeclarationAccepted({...declarationAccepted, indemnity: e.target.checked})}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${declarationAccepted.indemnity ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'}`}>
                    {declarationAccepted.indemnity && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span className="text-sm text-zinc-300">
                  <strong className="text-white">Indemnification:</strong> I agree to indemnify BrandStage against any claims arising from the content of my recording. BrandStage branding does not constitute endorsement or verification of my claims.
                </span>
              </label>
              
              {/* Data processing (DPDP/GDPR) */}
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="declaration-data-processing">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={declarationAccepted.data_processing}
                    onChange={(e) => setDeclarationAccepted({...declarationAccepted, data_processing: e.target.checked})}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${declarationAccepted.data_processing ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'}`}>
                    {declarationAccepted.data_processing && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span className="text-sm text-zinc-300">
                  <strong className="text-white">Privacy & Data Processing:</strong> I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#C41E3A] underline">Terms</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#C41E3A] underline">Privacy Policy</a>, and consent to BrandStage storing and processing my name, contact details and recording to produce and deliver my video (DPDP Act 2023 / GDPR).
                </span>
              </label>
            </div>
            
            {/* Disclaimer notice */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-6">
              <p className="text-amber-200 text-xs">
                <strong>Notice:</strong> Your video will include a disclaimer stating that views and claims are those of the speaker. BrandStage reserves the right to review all submissions before branded videos are delivered.
              </p>
            </div>
            
            <button
              onClick={handleDeclarationAccept}
              disabled={!allAccepted}
              className={`w-full py-4 rounded-lg font-bold mt-6 transition-colors flex items-center justify-center gap-2 ${
                allAccepted 
                  ? 'bg-[#C41E3A] hover:bg-[#A01830] text-white' 
                  : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
              }`}
              data-testid="booth-accept-declaration-btn"
            >
              {allAccepted ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  I Accept - Proceed to Recording
                </>
              ) : (
                <>
                  Please accept all terms above
                </>
              )}
            </button>
          </div>
          
          <button
            onClick={() => setStep(STEPS.FORM)}
            className="w-full text-zinc-500 hover:text-zinc-300 mt-4 py-2 transition-colors"
          >
            ← Back to Form
          </button>
        </div>
      </div>
    );
  }

  // Prompt Screen (Camera initializing + showing prompt)
  if (step === STEPS.PROMPT) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-6" data-testid="booth-prompt">
        <div className="max-w-4xl w-full">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Camera Preview */}
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden">
              {hasPermission === false ? (
                <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/95">
                  <div className="text-center p-6 max-w-sm">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CameraOff className="w-8 h-8 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Camera Issue</h3>
                    <p className="text-zinc-400 mb-4 text-sm">{error}</p>
                    <div className="bg-zinc-800 rounded-lg p-3 mb-4 text-left">
                      <p className="text-xs text-zinc-500 mb-2">Try these steps:</p>
                      <ul className="text-sm text-zinc-300 space-y-1">
                        <li>• Close Zoom, Teams, Skype, or other video apps</li>
                        <li>• Close other browser tabs using the camera</li>
                        <li>• Check if camera works in another app</li>
                      </ul>
                    </div>
                    <button
                      onClick={() => {
                        setHasPermission(null);
                        setError(null);
                        initCamera();
                      }}
                      className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold w-full"
                      data-testid="booth-retry-camera-btn"
                    >
                      Retry Camera
                    </button>
                  </div>
                </div>
              ) : hasPermission === null ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-12 h-12 animate-spin text-zinc-500" />
                </div>
              ) : (
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  webkit-playsinline="true"
                  className="w-full h-full object-cover mirror"
                  style={{ transform: 'scaleX(-1)' }}
                />
              )}
              
              {/* Camera controls */}
              {hasPermission && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  <button
                    onClick={toggleMute}
                    className={`p-3 rounded-full ${isMuted ? 'bg-red-500' : 'bg-zinc-800/80'} backdrop-blur`}
                  >
                    {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={toggleCamera}
                    className={`p-3 rounded-full ${isCameraOff ? 'bg-red-500' : 'bg-zinc-800/80'} backdrop-blur`}
                  >
                    {isCameraOff ? <CameraOff className="w-5 h-5" /> : <Camera className="w-5 h-5" />}
                  </button>
                </div>
              )}
            </div>
            
            {/* Prompt */}
            <div className="text-center md:text-left">
              <p className="text-[#C41E3A] text-sm font-semibold uppercase tracking-wider mb-4">
                Your Prompt
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                &quot;{currentPrompt}&quot;
              </h2>
              <p className="text-zinc-400 mb-6">
                You will have up to <span className="text-white font-bold">40 seconds</span> to record your response. 
                Take a breath, smile, and speak naturally!
              </p>
              
              {/* Helpful tips */}
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-6 text-left">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Tips</p>
                <ul className="text-sm text-zinc-400 space-y-1">
                  <li>• Record up to 45 seconds - final video will be ~60 sec with intro/outro</li>
                  <li>• Warning appears at 40 seconds - wrap up when you see it!</li>
                  <li>• You can review and retake as many times as you like</li>
                  <li>• Only your final submission will be sent</li>
                </ul>
              </div>
              
              <button
                onClick={startCountdown}
                disabled={!hasPermission}
                className="bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xl px-10 py-5 rounded-full font-bold transition-all inline-flex items-center gap-3"
                data-testid="booth-ready-btn"
              >
                <Play className="w-6 h-6" />
                I am Ready!
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Countdown Screen
  if (step === STEPS.COUNTDOWN) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="booth-countdown">
        <div className="text-center">
          <div className="text-[200px] font-black text-[#C41E3A] leading-none animate-pulse">
            {countdown}
          </div>
          <p className="text-2xl text-zinc-400 mt-4">Get ready...</p>
        </div>
      </div>
    );
  }

  // Recording Screen
  if (step === STEPS.RECORDING) {
    const progress = (duration / MAX_DURATION) * 100;
    const isWarning = duration >= WARN_AT;
    const countdown = MAX_DURATION - duration;
    
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="booth-recording">
        {/* Recording indicator */}
        <div className={`py-3 px-6 flex items-center justify-center gap-3 transition-colors ${isWarning ? 'bg-orange-500' : 'bg-red-600'}`}>
          <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
          <span className="text-white font-bold">{isWarning ? 'WRAP UP!' : 'RECORDING'}</span>
          <span className="text-white/80">{formatTime(duration)} / {formatTime(MAX_DURATION)}</span>
        </div>
        
        {/* Progress bar */}
        <div className="h-2 bg-zinc-800">
          <div 
            className={`h-full transition-all duration-1000 ${isWarning ? 'bg-orange-500' : 'bg-[#C41E3A]'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Video */}
        <div className="flex-1 relative">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            webkit-playsinline="true"
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          
          {/* Warning overlay when time is running out */}
          {isWarning && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center animate-pulse">
                <div className="text-8xl font-bold text-orange-500 drop-shadow-lg">{countdown}</div>
                <div className="text-2xl font-bold text-white mt-2 drop-shadow-lg">Wrap up now!</div>
              </div>
            </div>
          )}
          
          {/* Prompt overlay */}
          <div className={`absolute top-8 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur px-6 py-3 rounded-full ${isWarning ? 'opacity-50' : ''}`}>
            <p className="text-lg font-medium">&quot;{currentPrompt}&quot;</p>
          </div>
          
          {/* Stop button and guidance - only show after minimum recording time */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
            {duration >= MIN_BEFORE_STOP ? (
              <>
                <button
                  onClick={stopRecording}
                  className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors mx-auto shadow-lg ${isWarning ? 'bg-orange-500 hover:bg-orange-600 shadow-orange-500/30 animate-pulse' : 'bg-red-600 hover:bg-red-700 shadow-red-600/30'}`}
                  data-testid="booth-stop-btn"
                >
                  <div className="w-8 h-8 bg-white rounded" />
                </button>
                <p className="text-white mt-3 text-sm font-medium">
                  {isWarning ? 'Stop now to save!' : 'Tap to stop when done'}
                </p>
                <p className={`text-xs mt-1 ${isWarning ? 'text-orange-300 font-bold' : 'text-zinc-500'}`}>
                  {countdown} seconds remaining
                </p>
              </>
            ) : (
              <div className="bg-black/50 backdrop-blur px-6 py-4 rounded-xl">
                <p className="text-white text-lg font-medium">Keep going!</p>
                <p className="text-zinc-400 text-sm mt-1">
                  {MIN_BEFORE_STOP - duration} more seconds before you can stop
                </p>
                <div className="w-32 h-1 bg-zinc-700 rounded-full mt-3 mx-auto overflow-hidden">
                  <div 
                    className="h-full bg-[#C41E3A] transition-all duration-1000"
                    style={{ width: `${(duration / MIN_BEFORE_STOP) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Preview Screen
  if (step === STEPS.PREVIEW) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-6" data-testid="booth-preview">
        <div className="max-w-2xl w-full">
          {/* Header with guidance */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-zinc-800 px-4 py-2 rounded-full mb-4">
              <Play className="w-4 h-4 text-[#C41E3A]" />
              <span className="text-sm text-zinc-300">Press play to review your recording</span>
            </div>
            <h2 className="text-3xl font-bold mb-2">Review Your Video</h2>
            <p className="text-zinc-400">Watch it back and make sure you are happy with it before submitting</p>
          </div>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-400">{error}</p>
            </div>
          )}
          
          {/* Video Preview */}
          <div className="bg-zinc-900 rounded-2xl overflow-hidden mb-4">
            <video
              ref={previewRef}
              src={recordedUrl}
              controls
              className="w-full aspect-video"
              data-testid="booth-preview-video"
            />
          </div>
          
          {/* Recording info */}
          <div className="bg-zinc-800/50 rounded-lg p-3 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <Clock className="w-4 h-4" />
              <span>Duration: {formatTime(duration)}</span>
            </div>
            <div className="text-zinc-500 text-sm">
              Not happy? You can retake as many times as you need!
            </div>
          </div>
          
          {/* Action buttons with clear guidance */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <p className="text-center text-zinc-400 text-sm mb-4">
              Once you submit, your video will be sent to the BrandStage team
            </p>
            
            <div className="flex gap-4">
              <button
                onClick={handleRetake}
                className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 border border-zinc-700"
                data-testid="booth-retake-btn"
              >
                <RotateCcw className="w-5 h-5" />
                Retake Video
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
                data-testid="booth-submit-btn"
              >
                <Check className="w-5 h-5" />
                Submit Video
              </button>
            </div>
            
            <p className="text-center text-zinc-500 text-xs mt-4">
              Tip: Retake as many times as you like until you are satisfied
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Submitting Screen
  if (step === STEPS.SUBMITTING) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center" data-testid="booth-submitting">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-[#C41E3A] mx-auto mb-6" />
          <h2 className="text-2xl font-bold mb-2">Submitting Your Video...</h2>
          <p className="text-zinc-400">This will just take a moment</p>
        </div>
      </div>
    );
  }

  // Thank You Screen
  if (step === STEPS.THANKYOU) {
    // Determine if this is a free user (no access code = pending review)
    const isPendingReview = !accessCode && brandingStatus === 'pending_review';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-6" data-testid="booth-thankyou">
        <div className="max-w-lg w-full text-center">
          <div className={`w-24 h-24 ${isPendingReview ? 'bg-blue-500/20' : 'bg-green-500/20'} rounded-full flex items-center justify-center mx-auto mb-6`}>
            {isPendingReview ? (
              <Clock className="w-12 h-12 text-blue-500" />
            ) : (
              <Check className="w-12 h-12 text-green-500" />
            )}
          </div>
          <h1 className="text-4xl font-bold mb-4">
            {isPendingReview ? 'Submission Received!' : 'Thank You!'}
          </h1>
          <p className="text-xl text-zinc-400 mb-2">
            Hi <span className="text-white font-semibold">{formData.name}</span>,
          </p>
          
          {isPendingReview ? (
            <div className="text-zinc-400 mb-6">
              <p className="mb-4">
                Your video has been submitted for review. Our team will review it and send you 
                the <span className="text-white font-semibold">branded video within 24 hours</span>.
              </p>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-left">
                <p className="text-blue-300 text-sm mb-2">📋 What happens next?</p>
                <ol className="text-sm text-zinc-400 space-y-1 list-decimal list-inside">
                  <li>Our team reviews your video for content guidelines</li>
                  <li>Once approved, we add professional BrandStage branding</li>
                  <li>You receive the branded video via email</li>
                </ol>
              </div>
            </div>
          ) : (
            <p className="text-zinc-400 mb-6">
              We have received your video and will send a copy to <span className="text-white">{formData.email}</span> shortly.
            </p>
          )}
          
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
            <p className="text-zinc-500 text-sm mb-2">Recording from</p>
            <p className="text-xl font-bold">{formData.company}</p>
            <p className="text-zinc-500 text-sm mt-1">{formData.designation}</p>
          </div>
          
          {/* Branding Status Section - Only for paid users */}
          {!isPendingReview && brandingStatus && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6">
              {brandingStatus === 'pending' || brandingStatus === 'processing' ? (
                <div className="flex items-center justify-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-[#C41E3A]" />
                  <span className="text-zinc-300">Adding BrandStage branding to your video...</span>
                </div>
              ) : brandingStatus === 'complete' && brandedVideoUrl ? (
                <div>
                  <p className="text-green-400 font-semibold mb-3 flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    Your branded video is ready!
                  </p>
                  <a
                    href={brandedVideoUrl.replace("/upload/", "/upload/fl_attachment/")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-full font-bold transition-all"
                    data-testid="booth-download-branded-btn"
                  >
                    <Download className="w-5 h-5" />
                    Download Branded Video
                  </a>
                  <p className="text-zinc-500 text-xs mt-3">
                    Want your own custom branding? <a href="/#contact" className="text-[#C41E3A] hover:underline">Contact us</a>
                  </p>
                </div>
              ) : brandingStatus === 'skipped' ? (
                <p className="text-zinc-400 text-sm">
                  A copy of your video will be sent to your email.
                </p>
              ) : (
                <p className="text-zinc-400 text-sm">
                  Your video was submitted successfully. Check your email for the recording.
                </p>
              )}
            </div>
          )}
          
          {/* Show remaining recordings for event mode */}
          {eventInfo && (
            <div className="bg-[#C41E3A]/10 border border-[#C41E3A]/30 rounded-lg p-4 mb-6">
              <p className="text-[#C41E3A] font-semibold mb-1">{eventInfo.event_name}</p>
              <p className="text-zinc-400 text-sm">
                <Users className="w-4 h-4 inline mr-1" />
                {eventInfo.recordings_remaining > 0 
                  ? `${eventInfo.recordings_remaining} recordings remaining today`
                  : 'Daily limit reached'}
              </p>
            </div>
          )}
          
          {/* Different buttons based on mode */}
          {eventInfo ? (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {eventInfo.recordings_remaining > 0 && (
                <button
                  onClick={handleRecordAnotherSamePerson}
                  className="bg-[#C41E3A] hover:bg-[#A01830] text-white text-lg px-8 py-4 rounded-full font-bold transition-all inline-flex items-center justify-center gap-2"
                  data-testid="booth-same-person-btn"
                >
                  <Video className="w-5 h-5" />
                  Record Another (Me)
                </button>
              )}
              <button
                onClick={handleRecordAnother}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-lg px-8 py-4 rounded-full font-bold transition-all inline-flex items-center justify-center gap-2"
                data-testid="booth-another-btn"
              >
                <Users className="w-5 h-5" />
                Next Person
              </button>
            </div>
          ) : (
            <button
              onClick={handleRecordAnother}
              className="bg-[#C41E3A] hover:bg-[#A01830] text-white text-lg px-10 py-4 rounded-full font-bold transition-all inline-flex items-center gap-3"
              data-testid="booth-another-btn"
            >
              <Video className="w-5 h-5" />
              Record Another
            </button>
          )}
          
          <p className="text-zinc-600 text-sm mt-8">
            Powered by BrandStage Media
          </p>
          
          {/* Home Button */}
          <div className="mt-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
              data-testid="booth-home-btn"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
